import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep, set } from "lodash";
import { Box, CircularProgress, Modal } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CtaComponent from "../../../../components/reusable-ui/cta";
import uuid from "react-native-uuid";

import {
  CTA_SIZE,
  CTA_TYPE,
  MESSAGE_TYPE,
} from "../../../../data/constants";
import "../style.scss";
import { useDispatch } from "react-redux";
import { fetchCommonData, uploadReconFile } from "../../../../redux/slices";
import { ReconContext } from "../recon-context";
import { RECON_DATA } from "../constants";
import { METHODS } from "../../../../data/api-method-constants";
import { ENDPOINT } from "../../../../services/apiUrls";
import { AGGREGATOR_TYPE } from "../../../../data/outlet-status-constants";
import axios from "axios";
import { getLocalStorage, setLocalStorage } from "../../../../utils";
import { RECON_FILE_UPLOAD } from "../../../../data/local-storage-constants";
import { ToastNotifySuccess } from "../../../../helpers/toast-helper";
import { CrossIcon } from "../../../../components/reusable-ui/basic-modal";

enum FileUploadModalType {
  full,
  accordion
}

type UploadFileCredentials = {
  url: string;
  fields: Record<string, string>;
  fileName: string;
  attachmentId: string;
}

type ReconFileUploadResponseType = {
  data: UploadFileCredentials[];
  success: boolean;
}

const FileUploadModal: FC = () => {
  const { toggleUploadModal } = useContext(ReconContext);
  const showLeftInfo = !getLocalStorage(RECON_FILE_UPLOAD);
  useEffect(() => {
    // do not show left info again
    setLocalStorage(RECON_FILE_UPLOAD, true);
  }, [])

  return (
    <Modal className='font-inter' open={true} onClose={toggleUploadModal}>
      <Box className={`recon-file-upload-modal modal-center ${showLeftInfo ? "w-[873px]" : "w-[500px]"}  h-[80vh] flex rounded-[16px]`}>
        <CrossIcon handleClick={toggleUploadModal} className="absolute right-[-54px] top-[-50px]" />
        {showLeftInfo && <div className="overflow-scroll w-[372px]">
          <ReconFileUploadInfo type={FileUploadModalType.full} />
        </div>}
        <ReconFileUploadSection showLeftInfo={showLeftInfo} />
      </Box>
    </Modal>
  );
};

function ReconFileUploadInfo({ type = FileUploadModalType.full }: { type?: FileUploadModalType }) {
  const isFullInfoType = type === FileUploadModalType.full;

  function handleDownloadSample() {
    window.open(RECON_DATA.fileUpload.sampleFilelink, '_blank');
  }

  function renderNotes() {
    return <ul className={`p-12 pr-6 flex flex-col text-13 text-black/80 gap-14 ${isFullInfoType ? "list-none bg-[#FFF8D2] border border-[#0000001A] rounded-[6px] " : "list-disc pl-[25px]"}`}>
      <li className="">
        <div className="flex gap-10">
          {isFullInfoType && <div style={{ width: 20 }}><div className="rista-sprite pin-icon"></div></div>}
          {RECON_DATA.fileUpload.notes[0]}
        </div>
      </li>
      <li className="">
        <div className="flex gap-10">
          {isFullInfoType && <div style={{ width: 20 }}><div className="rista-sprite pin-icon"></div></div>}
          {RECON_DATA.fileUpload.notes[1]}
        </div>
      </li>
    </ul>
  }
  return <div className={`font-medium w-full ${isFullInfoType ? "h-full py-[42px] bg-grayBG" : ""} px-[20px] rounded-l-[16px] text-black`}>
    {isFullInfoType && <div className=" text-[18px] text-black">{RECON_DATA.fileUpload.header}</div>}
    <div className={`flex flex-col ${isFullInfoType ? "mt-[50px]" : ""} gap-8`}>
      <div className="text-[16px]">
        {isFullInfoType ? "" : "1. "}{RECON_DATA.fileUpload.steps[0].header}
      </div>
      <div className="text-black/80 font-normal text-[14px]">
        {RECON_DATA.fileUpload.steps[0].description}
      </div>
      <div className="text-black/80 font-normal text-[14px]">
        {RECON_DATA.fileUpload.steps[0].sampleFile?.[0]}
        <span onClick={handleDownloadSample} className="cursor-pointer text-activeColor underline font-medium">{RECON_DATA.fileUpload.steps[0].sampleFile?.[1]}</span>
        {RECON_DATA.fileUpload.steps[0].sampleFile?.[2]}
      </div>
      {renderNotes()}
    </div>
    <div className={`flex flex-col ${isFullInfoType ? 'mt-[50px]' : ''} gap-8`}>
      <div className="text-[16px]">
        {isFullInfoType ? "" : "2. "}{RECON_DATA.fileUpload.steps[1].header}
      </div>
      <div className="text-black/80 font-normal text-[14px]">
        {RECON_DATA.fileUpload.steps[1].description}
      </div>
    </div>
  </div>
}

enum FileState {
  InProgress = 'inprogress',
  Done = 'done',
  Failed = 'failed'
}
type FileType = {
  id: number;
  name: string;
  status: FileState;
  attachmentId?: string;
}

// 1. select files
// 2. call RECON.uploadReconFile endpoint
// 3. for each file upload file in the url and properties obtained from api call in step 2.
// 4. when all files are done, call RECON.reconUploadSuccess endpoint for each file
function ReconFileUploadSection({ showLeftInfo }: { showLeftInfo: boolean }) {
  const [files, setFiles] = useState<FileType[]>([]);
  const { toggleUploadModal } = useContext(ReconContext);
  const dispatch = useDispatch();

  function onStartReconCtaClick() {
    const groupId = uuid.v4();
    const reportIds: { fileName: string; reportId: string }[] = [];
    const filesToBeProcessed = files.filter(file => file.attachmentId && file.status === FileState.Done);
    //for each successfully uploaded file call reconUploadSuccess api
    filesToBeProcessed.forEach(file => {
      dispatch(fetchCommonData({
        url: ENDPOINT.RECON.reconUploadSuccess,
        method: METHODS.POST,
        body: {
          attachment: {
            fileName: file.name,
            id: file.attachmentId
          },
          groupId
        },
        callback: (res: any) => { updateReportId(file.name, res.reconUploadId || "") }
      }));
    });

    function updateReportId(fileName: string, reportId: string) {
      reportIds.push({ fileName, reportId });
      if (reportIds.length === filesToBeProcessed.length) {
        ToastNotifySuccess(RECON_DATA.fileUpload.reconStarted);
        toggleUploadModal();
      }
    }
  }

  async function filesUploader(newFiles: File[]) {
    const updatedFiles: FileType[] = newFiles.map(file => { return { id: file.lastModified, name: file.name, status: FileState.InProgress } });
    setFiles([...files, ...updatedFiles]);
    uploadApi(newFiles);
  }

  const enableReconCta = useMemo(() => {
    return files.some((file) => file.status === FileState.Done)
  }, [files])

  function updateFileStatus(fileId: number, state: FileState, attachmentId?: string) {
    setFiles(prevFiles => {
      return prevFiles.map(file =>
        file.id === fileId ? { ...file, status: state, attachmentId } : file
      );
    });
  }

  function uploadApi(files: File[]) {
    dispatch(fetchCommonData({
      url: ENDPOINT.RECON.uploadReconFile,
      method: METHODS.POST,
      body: {
        fileNames: files.map(file => file.name),
        aggregator: AGGREGATOR_TYPE.swiggy.id
      },
      callback: handleS3Upload
    }));

    function handleS3Upload(res: { code: string } | ReconFileUploadResponseType) {
      if ('code' in res) return;
      if (res.success) {
        res.data.forEach(uploadInfo => {
          const file = files.find(file => file.name === uploadInfo.fileName);
          if (file) {
            const fileForm = new FormData();
            Object.entries(uploadInfo.fields).forEach(([key, value]) => {
              fileForm.append(key, value);
            })
            fileForm.append('file', file);
            axios.post(uploadInfo.url, fileForm, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              validateStatus: function (status) {
                return true;
              },
            }).then(() => reconUploadSuccess(file.lastModified, uploadInfo.attachmentId)).catch((e) => {
              reconUploadSuccess(file.lastModified, uploadInfo.attachmentId); //TODO check conditions
              // if (e.response?.status === 204) {
              //   reconUploadSuccess(file.lastModified);
              // } else {
              //   updateFileStatus(file.lastModified, FileState.Failed);
              // }
            });
          }
        })
      }
    }

    function reconUploadSuccess(fileId: number, attachmentId: string) {
      updateFileStatus(fileId, FileState.Done, attachmentId);
    }
  }

  function deleteFile(fileId: number) {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter(file => file.id !== fileId)
      return updatedFiles;
    })
  }

  return <div className="flex flex-col p-24 pb-[0px] text-black relative overflow-scroll flex-grow">
    <div className="flex flex-col gap-12">
      <div className="font-semibold text-20">{RECON_DATA.fileUpload.chooseFileHeader}</div>
      <div className="text-16">{RECON_DATA.fileUpload.chooseFileDescription}</div>
    </div>
    <FileDropArea addFiles={filesUploader} />
    <div className="flex flex-col gap-6 mb-[20px]">
      <div className="text-[#868E96]">{RECON_DATA.fileUpload.supportedFormat}</div>
      <div className="text-[#868E96]">{RECON_DATA.fileUpload.maxSize}</div>
    </div>
    <UploadedFiles files={files} onDelete={deleteFile} />
    {!showLeftInfo && <Accordion className="mt-auto mb-[32px]">
      <AccordionSummary
        aria-controls="panel1-content"
        id="panel1-header"
      >
        {RECON_DATA.fileUpload.header}
      </AccordionSummary>
      <AccordionDetails className="">
        <ReconFileUploadInfo type={FileUploadModalType.accordion} />
      </AccordionDetails>
    </Accordion>}
    <div className="sticky bottom-[0px] right-[24px] w-full z-[50] bg-white pb-[24px] mt-auto">
      <CtaComponent
        ctaSize={CTA_SIZE.medium}
        ctaText={"Start Reconciliation"}
        ctaType={CTA_TYPE.active_cta}
        onCtaClick={onStartReconCtaClick}
        customClass={"w-full z-[10]"}
        isDisabled={!enableReconCta}
      ></CtaComponent>
    </div>
  </div>
}

function UploadedFiles({ files, onDelete }: { files: FileType[]; onDelete: (fileId: number) => void }) {
  if (!files.length) return <></>
  function getStatusIcon(status: FileState) {
    switch (status) {
      case FileState.Done: return 'ic-tick-filled-green';
      case FileState.Failed: return 'ic-circle-cross-red';
      case FileState.InProgress: return "";
    }
  }
  return <div className='flex flex-col gap-[16px] mt-24 pb-[60px]'>
    {files.map((file, fileIndex) =>
      <div className="border border-[#D6D6D6] flex rounded-[6px] p-[15px] items-center">
        {getStatusIcon(file.status) ? <div className={`rista-sprite ${getStatusIcon(file.status)}`}></div> : <CircularProgress className="h-[21px] w-[21px]" />}
        <div className="ml-16 max-w-[80%]">
          <div title={file.name} className="text-ellipsis overflow-hidden line-clamp-1">{file.name}</div>
          {file.status === FileState.InProgress && <div className="text-[#868E96] text-14">In progress...</div>}
        </div>
        <div className="rista-sprite ic-cross-grey p-4 ml-auto cursor-pointer" onClick={onDelete.bind(null, file.id)}></div>
      </div>)}
  </div>
}

function FileDropArea({ addFiles }: { addFiles: (files: File[]) => Promise<void>; }) {
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFileDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files); // Ensure proper conversion to File[]
    addFiles(droppedFiles);
  }

  function handleBrowseClick() {
    fileRef.current?.click();
  }

  function handleFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target?.files?.length) {
      addFiles(Array.from(event.target.files)); // Convert FileList to File[]
    }
  }

  return (
    <div
      onDragOver={(event) => event.preventDefault()}
      onDrop={handleFileDrop}
      className="mt-[20px] pt-[32px] pb-[43px] mb-[11px] flex gap-[10px] flex-col items-center justify-center bg-[#F8F8F8] border border-[#D6D6D6] rounded-[6px] border-dashed h-[220px] w-full"
    >
      <div className="rista-sprite upload-recon-sm"></div>
      <div>
        Drop your files or
        <span
          onClick={handleBrowseClick}
          className="cursor-pointer ml-4 text-blue underline font-semibold text-16"
        >
          Browse
        </span>
      </div>
      <input
        ref={fileRef}
        hidden
        type="file"
        multiple
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        onChange={handleFileInputChange}
      />
    </div>
  );
}

export default FileUploadModal;
